import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { DELTA_MAX, REASON_MAX } from '../../../../../Consts/licences.const';
import {
  AdjustLicencesDialogData,
  AdjustLicencesDialogResult,
} from '../../../Interfaces/licence-dialog.interface';

@Component({
  selector: 'app-adjust-licences-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatIconModule, TranslateModule],
  templateUrl: './adjust-licences-dialog.component.html',
  styleUrl: './adjust-licences-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdjustLicencesDialogComponent {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _dialogRef =
    inject<MatDialogRef<AdjustLicencesDialogComponent, AdjustLicencesDialogResult>>(MatDialogRef);

  readonly data = inject<AdjustLicencesDialogData>(MAT_DIALOG_DATA);

  readonly deltaMax = DELTA_MAX;
  readonly reasonMax = REASON_MAX;

  readonly availableComponents = this.data.components.filter((component) => component.is_available);

  readonly delta = signal<number>(1);

  readonly formGroup: FormGroup = this._formBuilder.group({
    component_type: [this.availableComponents[0]?.component_type ?? '', [Validators.required]],
    delta: [
      1,
      [Validators.required, Validators.min(-DELTA_MAX), Validators.max(DELTA_MAX)],
    ],
    reason: ['', [Validators.required, Validators.maxLength(REASON_MAX)]],
  });

  constructor() {
    this._registerDeltaSync();
  }

  confirm(): void {
    if (!this.formGroup.valid || Number(this.formGroup.value.delta) === 0) {
      return;
    }

    this._dialogRef.close({
      componentType: String(this.formGroup.value.component_type),
      delta: Number(this.formGroup.value.delta),
      reason: String(this.formGroup.value.reason).trim(),
    });
  }

  cancel(): void {
    this._dialogRef.close();
  }

  private _registerDeltaSync(): void {
    this.formGroup.controls['delta'].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this._applyDelta(Number(value)));
  }

  private _applyDelta(value: number): void {
    this.delta.set(Number.isFinite(value) ? value : 0);
  }
}
